import { TextBox } from "devextreme-react";
import Button from "devextreme-react/button";
import {
  Column,
  DataGrid,
  FilterRow,
  Item,
  Toolbar,
} from "devextreme-react/data-grid";
import { ScrollView } from "devextreme-react/scroll-view";
import { CustomStore, DataSource } from "devextreme/common/data";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { getProjectsFromCompany } from "../../api/projects";
import ProjectSupervisorsListPopup from "../../components/project-supervisors-list-popup/ProjectSupervisorsListPopup";
import { useAuth } from "../../contexts/auth";
import { useCompanyContext } from "../../contexts/company";
import { Project, RoleEnum } from "../../types";
import { useScreenSize } from "../../utils/media-query";
import "./Projects.scss";

export default function Projects() {
  const { t } = useTranslation();
  const { companyId } = useCompanyContext();
  const { user } = useAuth();
  const { isXSmall, isSmall } = useScreenSize();
  const isMobile = isXSmall || isSmall;

  const [
    projectForWhichListOfSupervisorsIsToBeDisplayed,
    setProjectForWhichListOfSupervisorsIsToBeDisplayed,
  ] = useState<Project>();
  const [filter, setFilter] = useState("");
  const [mobileProjects, setMobileProjects] = useState<Project[]>([]);

  const isHeadAdministrator =
    user?.roles?.includes(RoleEnum.AdministratorGlowny) ?? false;

  const dataSource = useMemo(() => {
    return new DataSource({
      store: new CustomStore({
        loadMode: "raw",
        key: "id",
        load: async () => {
          const projects = await getProjectsFromCompany(
            !isHeadAdministrator
              ? undefined
              : companyId === 0
                ? undefined
                : companyId,
          );

          return projects;
        },
      }),
    });
  }, [companyId, isHeadAdministrator]);

  useEffect(() => {
    if (!isMobile) return;
    let cancelled = false;
    (async () => {
      try {
        const projects = await getProjectsFromCompany(
          !isHeadAdministrator
            ? undefined
            : companyId === 0
              ? undefined
              : companyId,
        );
        if (!cancelled) setMobileProjects(projects ?? []);
      } catch {
        if (!cancelled) setMobileProjects([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId, isHeadAdministrator, isMobile]);

  const filteredProjects = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return mobileProjects;
    return mobileProjects.filter((p: any) => {
      const acronym = (p.acronym ?? "").toLowerCase();
      const company = (p.companyName ?? "").toLowerCase();
      return acronym.includes(q) || company.includes(q);
    });
  }, [mobileProjects, filter]);

  return (
    <ScrollView useNative>
      <div className="p-6">
        {projectForWhichListOfSupervisorsIsToBeDisplayed ? (
          <ProjectSupervisorsListPopup
            project={projectForWhichListOfSupervisorsIsToBeDisplayed}
            onClose={() =>
              setProjectForWhichListOfSupervisorsIsToBeDisplayed(undefined)
            }
          />
        ) : null}
        <div className="mb-2 text-2xl font-bold leading-none">
          {t("project.projects")}
        </div>
        <div className="mb-1 text-base">{t("project.headerText")}</div>
        {isMobile ? (
          <div className="projects-mobile">
            <div className="projects-mobile-filter">
              <TextBox
                value={filter}
                onValueChanged={(e) => setFilter(e.value ?? "")}
                placeholder={t("searchPlaceholder")}
                showClearButton
              />
            </div>
            <div className="projects-mobile-list">
              {filteredProjects.length === 0 ? (
                <div className="projects-mobile-empty">{t("No data")}</div>
              ) : (
                filteredProjects.map((p: any) => (
                  <div key={p.id} className="projects-mobile-card">
                    <div className="projects-mobile-card-main">
                      <div className="projects-mobile-card-acronym">
                        {p.acronym || "—"}
                      </div>
                      {p.companyName && (
                        <div className="projects-mobile-card-company">
                          {p.companyName}
                        </div>
                      )}
                    </div>
                    <div className="projects-mobile-card-actions">
                      <Button
                        icon="group"
                        stylingMode="text"
                        hint={t("project.addUsers")}
                        onClick={() =>
                          setProjectForWhichListOfSupervisorsIsToBeDisplayed(p)
                        }
                      />
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        ) : (
          <DataGrid
            dataSource={dataSource}
            showBorders
            allowColumnResizing
            allowColumnReordering
            searchPanel={{
              visible: true,
              placeholder: t("searchPlaceholder"),
            }}
            paging={{
              pageSize: 10,
            }}
            pager={{
              visible: true,
              showInfo: true,
              showNavigationButtons: true,
            }}
          >
            <Toolbar>
              <Item
                name="searchPanel"
                location="before"
                locateInMenu="auto"
                cssClass="datagrid-search-panel-without-margin-left"
              />
            </Toolbar>
            <FilterRow visible />
            <Column
              dataField="companyName"
              dataType="string"
              caption={t("project.companyName")}
              width={300}
            />
            <Column
              dataField="acronym"
              dataType="string"
              caption={t("project.acronym")}
            />
            <Column
              alignment="right"
              type="buttons"
              buttons={[
                {
                  icon: "group",
                  hint: t("project.addUsers"),
                  onClick: (event) => {
                    setProjectForWhichListOfSupervisorsIsToBeDisplayed(
                      event.row?.data,
                    );
                  },
                },
              ]}
            />
          </DataGrid>
        )}
      </div>
    </ScrollView>
  );
}

/*
import { ScrollView } from "devextreme-react";
import Button, { IButtonOptions } from "devextreme-react/button";
import { CheckBox } from "devextreme-react/check-box";
import {
  DataGrid,
  Item as DataGridItem,
  FilterRow,
  HeaderFilter,
  Toolbar,
} from "devextreme-react/data-grid";
import { List, ListRef } from "devextreme-react/list";
import { Popup, ToolbarItem } from "devextreme-react/popup";
import { Column } from "devextreme-react/tree-list";
import { DataSource } from "devextreme/common/data";
import notify from "../../lib/notify";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  downloadEmployeeProjects,
  DownloadProjectEmployee,
  downloadProjects,
} from "../../api/calendarData";
import {
  addEmployeeToProject,
  deleteEmployeeFromProject,
  fetchDataToProject,
  getManagersToProject,
} from "../../api/projects";
import { fetchManagers } from "../../api/users";
import { useAuth } from "../../contexts/auth";
import { useCompanyContext } from "../../contexts/company";
import { useLoader } from "../../hooks/useLoading";
import { RoleEnum } from "../../types";
import "./Projects.scss";

export const Projects = () => {
  const [showOnlyManagers, setShowOnlyManagers] = useState(false);
  const [projects, setProjects] = useState<
    {
      id: number;
      companyName: string;
      acronym: string;
    }[]
  >([]);

  const { t } = useTranslation();
  const [projectId, setProjectId] = useState<number>(1);
  const [popupVisible, setPopupVisible] = useState(false);
  const [popupUserVisible, setPopupUserVisible] = useState(false);
  const [managersToProject, setManagersToProject] = useState<Employee[]>();
  const { user } = useAuth();
  const [employeeProject, setEmployeeProject] = useState<
    { companyId: number; employeeId: number; name: string; surname: string }[]
  >([]);

  const [managersInProject, setManagersInProject] =
    useState<
      { companyId: number; employeeId: number; name: string; surname: string }[]
    >();

  const [employeeProjectToAdd, setEmployeeProjectToAdd] = useState<
    { companyId: number; employeeId: number; name: string; surname: string }[]
  >([]);

  const { companyId } = useCompanyContext();
  const role = user?.roles;
  const isAdmin = role && role.includes(RoleEnum.AdministratorGlowny);
  const isManager = role && role.includes(RoleEnum.Kierownik);
  const isHeadManager = role && role.includes(RoleEnum.Manager);
  const loader = useLoader();

  const listRef = useRef<ListRef>(null);
  const [
    isSubordinateAddFormSubmitButtonDisabled,
    setIsSubordinateAddFormSubmitButtonDisabled,
  ] = useState(true);

  const fetchDataForProject = async () => {
    let headAdministratorCompanyId: number | undefined = undefined;
    if (isAdmin) {
      headAdministratorCompanyId = user.companyId;
    }

    try {
      const responseName = await downloadProjects(headAdministratorCompanyId);

      if (responseName.data && Array.isArray(responseName.data)) {
        setProjects(responseName.data);
      } else {
        const namesList = responseName.data.data || [];
        setProjects(namesList);
      }
    } catch (err) {
      notify(`${t("Error:")} ${err || t("Unknown error")}`, "error");
    }
  };

  const fetchProjectsForEmployee = async () => {
    const request = await downloadEmployeeProjects(
      Number(user?.employeeId),
      Number(user?.companyId),
    );
    setProjects(request.data);
  };

  const fetchEmployeeToProject = async (id: number) => {
    try {
      let company = 0;

      if (companyId === 0) {
        company = user?.companyId || 0;
      } else {
        company = companyId;
      }
      const response = await DownloadProjectEmployee(id, company);

      if (response.isOk) {
        const sorted = response.data.sort(
          (a, b) =>
            a.surname.localeCompare(b.surname, "pl", { sensitivity: "base" }) ||
            a.name.localeCompare(b.name, "pl", { sensitivity: "base" }),
        );
        setEmployeeProject(sorted);
      } else {
        notify(
          `${t("Error:")} ${response.message || t("Unknown error")}`,
          "error",
        );
      }
    } catch (error) {
      notify(`${t("Error:")} ${error || t("Unknown error")}`, "error");
    }
  };

  const getManagers = async (companyId: number) => {
    try {
      const result = await fetchManagers(companyId);

      if (result.isOk) {
        const managers: Employee[] =
          result.data?.map((e) => ({
            employeeId: e.id,
            companyId: e.companyId,
            name: e.firstName,
            surname: e.lastName,
          })) ?? [];

        const sortedManagers = managers.sort(
          (a, b) =>
            a.surname.localeCompare(b.surname, "pl", { sensitivity: "base" }) ||
            a.name.localeCompare(b.name, "pl", { sensitivity: "base" }),
        );

        setManagersToProject(sortedManagers);
      } else {
        setManagersToProject(undefined);
        notify(`${t("Error:")} ${result.error || t("Unknown error")}`, "error");
      }
    } catch (error: unknown) {
      if (error instanceof Error) {
        setManagersToProject(undefined);
        notify(
          `${t("Error:")} ${error.message || t("Unknown error")}`,
          "error",
        );
      }
    }
  };

  const DOWNLOADmanagersToProject = async (
    companyId: number,
    projectId: number,
  ) => {
    if (companyId !== 0 && isAdmin) {
      try {
        const result = await getManagersToProject(companyId, projectId);

        if (result.isOk) {
          const managers = Array.isArray(result.data)
            ? result.data.flat().map((d) => ({
                employeeId: d.id,
                companyId: d.companyId,
                name: d.firstName,
                surname: d.lastName,
                isRegistered: d.isRegistered,
              }))
            : [];

          const sortedManagers = managers.sort(
            (a, b) =>
              a.surname.localeCompare(b.surname, "pl", {
                sensitivity: "base",
              }) || a.name.localeCompare(b.name, "pl", { sensitivity: "base" }),
          );

          setManagersInProject(sortedManagers);
        } else {
          setManagersInProject(undefined);
          notify(
            `${t("Error:")} ${result.error || t("Unknown error")}`,
            "error",
          );
        }
      } catch (error: unknown) {
        if (error instanceof Error) {
          setManagersInProject(undefined);
          notify(
            `${t("Error:")} ${error.message || t("Unknown error")}`,
            "error",
          );
        }
      }
    }
  };

  const removeEmployee = async (
    projectId: number,
    employeeId: number,
    companyId: number,
  ) => {
    try {
      const result = await deleteEmployeeFromProject(
        projectId,
        companyId,
        employeeId,
      );
      if (result.isOk) {
        notify(t("User deleted."), "success");
        fetchEmployeeToProject(projectId);
      } else {
        notify(`${t("Error:")} ${result.error || t("Unknown error")}`, "error");
      }
    } catch (error: unknown) {
      if (error instanceof Error) {
        notify(
          `${t("Error:")} ${error.message || t("Unknown error")}`,
          "error",
        );
      }
    }
  };

  const addEmployee = async (projectId: number, employees: Employee[]) => {
    try {
      const result = await addEmployeeToProject(projectId, employees);
      if (result.isOk) {
        notify(t("User added."), "success");
        await fetchEmployeeToProject(projectId);
      } else {
        notify(`${t("Error:")} ${result.error || t("Unknown error")}`, "error");
      }
    } catch (error: unknown) {
      if (error instanceof Error) {
        notify(
          `${t("Error:")} ${error.message || t("Unknown error")}`,
          "error",
        );
      }
    }
  };

  const fetchEmployees = async () => {
    try {
      let company = 0;

      if (companyId === 0) {
        company = user?.companyId || 0;
      } else {
        company = companyId;
      }
      const result = await fetchDataToProject(company, projectId);
      if (result.isOk) {
        setEmployeeProjectToAdd(result.data);
      } else {
        notify(`${t("Error:")} ${result.error || t("Unknown error")}`, "error");
      }
    } catch (error: unknown) {
      if (error instanceof Error) {
        notify(
          `${t("Error:")} ${error.message || t("Unknown error")}`,
          "error",
        );
      }
    }
  };

  const acceptPopup = (project: any) => {
    setProjectId(project.id);
    setPopupVisible(true);
    fetchEmployeeToProject(project.id);
    DOWNLOADmanagersToProject(companyId, projectId);
  };

  const deletePopupUser = async (data: any) => {
    await removeEmployee(
      projectId,
      Number(data?.employeeId),
      Number(data?.companyId),
    );
    await fetchEmployeeToProject(projectId);
    await DOWNLOADmanagersToProject(companyId, projectId);
  };

  const addPopupUser = (user: any) => {
    if (isAdmin) {
      getManagers(companyId);
    } else {
      fetchEmployees();
    }
    setPopupUserVisible(true);
  };

  useEffect(() => {
    const loadProjects = async () => {
      loader?.showLoader();
      try {
        if (isManager || user?.isTimeManager) {
          await fetchProjectsForEmployee();
        } else {
          await fetchDataForProject();
        }
      } catch (error) {
        console.error(error);
      } finally {
        loader?.hideLoader();
      }
    };

    loadProjects();
  }, [isManager, user?.isTimeManager]);

  useEffect(() => {
    const fetchEmployee = async () => {
      loader?.showLoader();
      try {
        await fetchEmployeeToProject(projectId);
      } catch (error) {
        console.error(error);
      } finally {
        loader?.hideLoader();
      }
    };
    fetchEmployee();
  }, [projectId]);

  useEffect(() => {
    if (!isAdmin) {
      setShowOnlyManagers(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    const fetchManagersToProject = async () => {
      loader?.showLoader();
      try {
        await DOWNLOADmanagersToProject(companyId, projectId);
        await getManagers(companyId);
      } catch (error) {
        console.error(error);
      } finally {
        loader?.hideLoader();
      }
    };
    fetchManagersToProject();
  }, [projectId, isAdmin!!!]);

  return (
    <ScrollView useNative>
      <div className="p-6">
        <div className="mb-2 text-2xl font-bold leading-none">
          {t("project.projects")}
        </div>
        <div className="mb-1 text-base">{t("project.headerText")}</div>
        <DataGrid
          dataSource={projects}
          keyExpr="id"
          showBorders
          allowColumnResizing
          allowColumnReordering
          searchPanel={{
            visible: true,
            placeholder: t("searchPlaceholder"),
          }}
          paging={{
            pageSize: 10,
          }}
          pager={{
            visible: true,
            showInfo: true,
            showNavigationButtons: true,
          }}
        >
          <HeaderFilter visible />
          <Toolbar>
            <DataGridItem
              name="searchPanel"
              location="before"
              locateInMenu="auto"
              cssClass="datagrid-search-panel-without-margin-left"
            />
          </Toolbar>
          <FilterRow visible />
          <Column
            dataField="companyName"
            dataType="string"
            caption={t("project.companyName")}
            width={300}
          />
          <Column
            dataField="acronym"
            dataType="string"
            caption={t("project.acronym")}
          />
          <Column
            alignment="right"
            type="buttons"
            buttons={[
              {
                icon: "group",
                onClick: (e) => {
                  if (e.row && !e.row.data.isRegistered) {
                    acceptPopup(e.row.data);
                  }
                },
                disabled: (e) => (e.row ? e.row.data.isRegistered : true),
                hint: t("project.addUsers"),
              },
            ]}
          />
        </DataGrid>
        {popupVisible ? (
          <Popup
            visible
            onHiding={() => {
              setPopupVisible(false);
              setShowOnlyManagers(false);
            }}
            maxWidth={800}
            height="auto"
            maxHeight="80vh"
            title={t("project.addUsers")}
            showCloseButton
            dragEnabled
          >
            <div className="popup-projects">
              <div className="data-grid-projects">
                <DataGrid
                  dataSource={
                    isAdmin && showOnlyManagers
                      ? managersInProject
                      : employeeProject
                  }
                  keyExpr="employeeId"
                  showBorders
                  allowColumnReordering
                  allowColumnResizing
                  searchPanel={{
                    visible: true,
                    placeholder: t("searchPlaceholder"),
                  }}
                  paging={{
                    pageSize: 10,
                  }}
                  pager={{
                    visible: true,
                    showInfo: true,
                    showNavigationButtons: true,
                  }}
                  sorting={{
                    showSortIndexes: false,
                  }}
                >
                  <FilterRow visible />
                  <Column
                    dataField="surname"
                    dataType="string"
                    caption={t("project.surname")}
                    sortOrder="asc"
                    sortIndex={0}
                    minWidth={200}
                  />
                  <Column
                    dataField="name"
                    dataType="string"
                    caption={t("project.name")}
                    sortOrder="asc"
                    sortIndex={1}
                    minWidth={200}
                  />
                  <Column
                    type="buttons"
                    alignment="right"
                    name="deleteUser"
                    buttons={[
                      {
                        icon: "trash",
                        onClick: (e) => {
                          if (e.row && !e.row.data.isRegistered) {
                            deletePopupUser(e.row.data);
                          }
                        },
                        disabled: (e) =>
                          e.row ? e.row.data.isRegistered : true,
                        hint: t("project.removeEmployee"),
                      },
                    ]}
                  />
                  <Toolbar>
                    <DataGridItem
                      location="before"
                      locateInMenu="auto"
                      name="searchPanel"
                      cssClass="datagrid-search-panel-without-margin-left"
                    />
                    {isAdmin && companyId !== 0 ? (
                      <DataGridItem location="center" locateInMenu="auto">
                        <CheckBox
                          id="show-only-managers"
                          value={showOnlyManagers}
                          onValueChange={(value) => {
                            if (value !== undefined && value !== null) {
                              setShowOnlyManagers(value);

                              if (value) {
                                DOWNLOADmanagersToProject(companyId, projectId);
                              } else {
                                fetchEmployees();
                              }
                            }
                          }}
                          text={t("project.showOnlyManagers")}
                        />
                      </DataGridItem>
                    ) : null}
                    {!isHeadManager ? (
                      <DataGridItem location="after" locateInMenu="auto">
                        <Button
                          id="addUser"
                          onClick={addPopupUser}
                          text={
                            isAdmin
                              ? t("project.addManager")
                              : t("project.addUser")
                          }
                          type="default"
                          disabled={companyId === 0 && isAdmin ? true : false}
                        />
                      </DataGridItem>
                    ) : null}
                  </Toolbar>
                </DataGrid>
              </div>
            </div>
          </Popup>
        ) : null}
        {popupUserVisible ? (
          <Popup
            visible
            onHiding={() => setPopupUserVisible(false)}
            maxWidth={400}
            height="auto"
            maxHeight="80vh"
            title={
              isAdmin ? t("project.addManager") : t("project.transferEmployee")
            }
            showCloseButton
            dragEnabled
          >
            <List
              ref={listRef}
              dataSource={
                new DataSource({
                  store: isAdmin
                    ? managersToProject
                        ?.filter(
                          (manager) =>
                            !managersInProject?.some(
                              (m) => m.employeeId === manager.employeeId,
                            ),
                        )
                        .map((e) => ({
                          value: JSON.stringify({
                            employeeId: e.employeeId,
                            companyId: e.companyId,
                          }),
                          label: `${e.surname} ${e.name}`,
                        }))
                    : employeeProjectToAdd.map((e) => ({
                        value: JSON.stringify({
                          employeeId: e.employeeId,
                          companyId: e.companyId,
                        }),
                        label: `${e.surname} ${e.name}`,
                      })),
                  paginate: false,
                })
              }
              keyExpr="value"
              displayExpr="label"
              searchExpr="label"
              showSelectionControls
              selectionMode="all"
              selectByClick
              searchEnabled
              height={400}
              onSelectedItemsChange={(values) =>
                setIsSubordinateAddFormSubmitButtonDisabled(values.length === 0)
              }
            />
            <ToolbarItem
              location="after"
              toolbar="bottom"
              widget="dxButton"
              options={
                {
                  text: t("project.send"),
                  stylingMode: "contained",
                  type: "default",
                  onClick: async () => {
                    const selectedItemKeys = listRef.current
                      ?.instance()
                      .option("selectedItemKeys") as string[] | undefined;
                    if (selectedItemKeys) {
                      if (selectedItemKeys.length > 0) {
                        const selected: Employee[] | [] = selectedItemKeys.map(
                          (item) => JSON.parse(item),
                        );
                        // console.log(selected);
                        await addEmployee(projectId, selected).then(() => {
                          DOWNLOADmanagersToProject(companyId, projectId);
                          setPopupUserVisible(false);
                        });
                      }
                    }
                  },
                } as IButtonOptions
              }
              disabled={isSubordinateAddFormSubmitButtonDisabled}
            />
          </Popup>
        ) : null}
      </div>
    </ScrollView>
  );
};

export type Employee = {
  employeeId: number;
  companyId: number;
  name: string;
  surname: string;
};
*/
